import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static panelHeightStorageKey = "compoundActionPanelHeight"

  static targets = [
    "buildingsContent",
    "buildingsInner",
    "buildingsChevron",
    "actionPanel",
    "actionPanelContent",
    "actionPanelChevron",
    "resizeHandle"
  ]

  connect() {
    this.buildingsCollapsed = false
    this.actionPanelCollapsed = false
    this.isResizing = false
    this.desktopMediaQuery = window.matchMedia("(min-width: 1024px)")
    this._handleViewportChange = this._applyLayout.bind(this)
    this.desktopMediaQuery.addEventListener("change", this._handleViewportChange)
    this._handlePointerMove = this._onPointerMove.bind(this)
    this._handlePointerUp = this._stopResize.bind(this)
    this.savedPanelHeight = this._loadSavedPanelHeight()

    this.resizeObserver = new ResizeObserver(() => this._applyLayout())
    this.resizeObserver.observe(this.element)
    this.resizeObserver.observe(this.buildingsInnerTarget)

    this._applyLayout()
  }

  disconnect() {
    this.desktopMediaQuery.removeEventListener("change", this._handleViewportChange)
    this.resizeObserver?.disconnect()
    this._removePointerListeners()
  }

  toggleBuildings() {
    this.buildingsCollapsed = !this.buildingsCollapsed
    this._applyLayout()
  }

  toggleActionPanel() {
    this.actionPanelCollapsed = !this.actionPanelCollapsed
    this._applyLayout()
  }

  startResize(event) {
    if (!this.desktopMediaQuery.matches || this.buildingsCollapsed || this.actionPanelCollapsed) return

    event.preventDefault()
    this.isResizing = true
    this.resizeHandleTarget.setPointerCapture?.(event.pointerId)
    document.body.classList.add("select-none", "cursor-row-resize")
    window.addEventListener("pointermove", this._handlePointerMove)
    window.addEventListener("pointerup", this._handlePointerUp)
  }

  resetActionPanelHeight() {
    this.savedPanelHeight = null
    window.localStorage.removeItem(this.constructor.panelHeightStorageKey)
    this._applyLayout()
  }

  _applyLayout() {
    const bc = this.buildingsContentTarget
    const bi = this.buildingsInnerTarget
    const bch = this.buildingsChevronTarget
    const ap = this.actionPanelTarget
    const apc = this.actionPanelContentTarget
    const apch = this.actionPanelChevronTarget
    const rh = this.resizeHandleTarget
    const heightClasses = ["h-48", "sm:h-56"]
    const isDesktop = this.desktopMediaQuery.matches

    bc.classList.toggle("hidden", this.buildingsCollapsed)
    bch.textContent = this.buildingsCollapsed ? "▲" : "▼"

    apc.classList.toggle("hidden", this.actionPanelCollapsed)
    apch.textContent = this.actionPanelCollapsed ? "▼" : "▲"
    rh.classList.toggle("hidden", !isDesktop || this.buildingsCollapsed || this.actionPanelCollapsed)

    // Expand action panel to fill screen only when buildings hidden and panel open
    const shouldExpand = this.buildingsCollapsed && !this.actionPanelCollapsed

    if (shouldExpand) {
      ap.classList.remove("shrink-0")
      ap.classList.add("flex-1", "flex", "flex-col", "min-h-0")
      heightClasses.forEach(c => apc.classList.remove(c))
      apc.classList.add("flex-1", "min-h-0")
      bc.style.flex = ""
      bc.style.height = ""
      ap.style.height = ""
    } else {
      ap.classList.add("shrink-0")
      ap.classList.remove("flex-1", "flex", "flex-col", "min-h-0")
      apc.classList.remove("flex-1", "min-h-0")

      bc.style.flex = ""
      bc.style.height = ""
      ap.style.height = ""

      if (!this.actionPanelCollapsed) {
        heightClasses.forEach(c => apc.classList.add(c))
      }
    }

    if (isDesktop && !this.buildingsCollapsed && !this.actionPanelCollapsed) {
      const tabsHeight = this._tabStripHeight()
      const sectionHeaderHeight = this.element.firstElementChild?.offsetHeight || 0
      const availableHeight = Math.max(this.element.clientHeight - sectionHeaderHeight, 0)
      const desktopMinPanelHeight = Math.max(224, tabsHeight + 120)
      const minGridHeight = 180
      const naturalGridHeight = bi.offsetHeight + this._verticalPadding(bc)
      const autoGridHeight = Math.min(naturalGridHeight, Math.max(availableHeight - desktopMinPanelHeight, minGridHeight))
      const autoPanelHeight = Math.max(availableHeight - autoGridHeight, desktopMinPanelHeight)
      const maxPanelHeight = Math.max(availableHeight - minGridHeight, desktopMinPanelHeight)
      const panelHeight = this._clampPanelHeight(this.savedPanelHeight ?? autoPanelHeight, desktopMinPanelHeight, maxPanelHeight)
      const gridHeight = Math.max(availableHeight - panelHeight, minGridHeight)

      bc.style.flex = "0 0 auto"
      bc.style.height = `${gridHeight}px`
      ap.style.height = `${panelHeight}px`
    }
  }

  _onPointerMove(event) {
    if (!this.isResizing) return

    const rect = this.element.getBoundingClientRect()
    const sectionHeaderHeight = this.element.firstElementChild?.offsetHeight || 0
    const availableHeight = Math.max(rect.height - sectionHeaderHeight, 0)
    const nextPanelHeight = rect.bottom - event.clientY
    const tabsHeight = this._tabStripHeight()
    const minPanelHeight = Math.max(224, tabsHeight + 120)
    const maxPanelHeight = Math.max(availableHeight - 180, minPanelHeight)

    this.savedPanelHeight = this._clampPanelHeight(nextPanelHeight, minPanelHeight, maxPanelHeight)
    this._savePanelHeight()
    this._applyLayout()
  }

  _stopResize() {
    if (!this.isResizing) return

    this.isResizing = false
    document.body.classList.remove("select-none", "cursor-row-resize")
    this._removePointerListeners()
  }

  _removePointerListeners() {
    window.removeEventListener("pointermove", this._handlePointerMove)
    window.removeEventListener("pointerup", this._handlePointerUp)
  }

  _tabStripHeight() {
    const handleHeight = this.resizeHandleTarget.classList.contains("hidden") ? 0 : this.resizeHandleTarget.offsetHeight
    const tabsHeight = this.actionPanelTarget.children[1]?.offsetHeight || 0

    return handleHeight + tabsHeight
  }

  _clampPanelHeight(height, min, max) {
    return Math.min(Math.max(height, min), max)
  }

  _loadSavedPanelHeight() {
    const rawValue = window.localStorage.getItem(this.constructor.panelHeightStorageKey)
    const parsedValue = Number(rawValue)

    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null
  }

  _savePanelHeight() {
    if (this.savedPanelHeight == null) return

    window.localStorage.setItem(this.constructor.panelHeightStorageKey, String(Math.round(this.savedPanelHeight)))
  }

  _verticalPadding(element) {
    const styles = window.getComputedStyle(element)

    return parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom)
  }
}
